import axios from "axios";
import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {BrandService} from "../gen/kitecorp/transferify/storefront/v1/brand_pb";
import {brandOf} from "./brandMessages";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";

/**
 * The sender's own brand (api#98). Separate from {@link AccountApi} because the whole surface is
 * PREMIUM-gated and the api enforces that on every write — a lower tier gets a 400 here, never a silent
 * no-op, so the client gate is UX only.
 */

/**
 * The `BrandService` payload. Every field is nullable: a brand can be colour-only,
 * logo-only, or unset. `onPrimaryColor` is SERVER-DERIVED from `primaryColor` and is not settable —
 * see `account/brand/brandColors.ts` for why the foreground is derived rather than chosen.
 */
export type CustomerBrand = {
	/** Durable, unauthenticated logo URL. Not a presigned link: it must survive inside a delivered email. */
	logoUrl: string | null;
	primaryColor: string | null;
	onPrimaryColor: string | null;
};

/* OpenAPI conformance (#208): `tsc -b` fails when the brand drifts from the generated spec type. */
export type _CustomerBrand = AssertNoSpecDrift<SpecDrift<CustomerBrand, Schemas["CustomerBrand"]>>;

const brandClient = () => createClient(BrandService, apiTransport);

export class AccountBrandApi {
	static async getBrand(): Promise<CustomerBrand> {
		return brandOf((await brandClient().getBrand({})).brand);
	}

	/** `null` leaves the colour field out of the request, which clears the colour and keeps the logo. */
	static async setColor(primaryColor: string | null): Promise<CustomerBrand> {
		return brandOf((await brandClient().setBrandColor({primaryColor: primaryColor ?? undefined})).brand);
	}

	/**
	 * Stays on REST: the logo is a multipart body and the contract has no message for one. The multipart uploads are
	 * batch 7 of kitecorp/transferify#418, which kitecorp/transferify-api-service#3161 tracks.
	 */
	static async setLogo(logo: File): Promise<CustomerBrand> {
		const body = new FormData();
		body.append("logo", logo);
		const {data} = await axios.put<CustomerBrand>("/account/brand/logo", body, {
			headers: {"Content-Type": "multipart/form-data"},
		});
		return data;
	}

	static async resetBrand(): Promise<CustomerBrand> {
		return brandOf((await brandClient().resetBrand({})).brand);
	}
}
